import {
  ASSIGNMENTS_LOADED,
  ASSISTANT_SESSION_LOADED,
  CARE_TEAM_LOADED,
  INTERVENTIONS_CLEARED,
  InterventionActionsType,
  LIBRARY_LOADED,
  MEDICATIONS_LOADED,
  PROGRAM_CARE_TEAM_LOADED,
  PROGRAM_DETAIL_LOADED,
  PROGRAM_LIBRARY_LOADED,
  PROGRAM_MODULES_LOADED,
  PROGRAM_NEXT_LOADED,
  RUN_LOADED,
  STEP_CONTENT_LOADED
} from './actions';
import {
  IInterventionActionHandler,
  IInterventionInitialState,
  ILibraryLoadedPayload,
  IPatientAssignment,
  IPatientCareTeamMember,
  IPatientMedication,
  IProgramCareTeamLoadedPayload,
  IProgramDetailLoadedPayload,
  IProgramLibraryLoadedPayload,
  IProgramModulesLoadedPayload,
  IProgramNextLoadedPayload,
  IRunLoadedPayload,
  IStepContentLoadedPayload
} from './interfaces';

const InterventionInitialState: IInterventionInitialState = {
  assignments: [],
  run: {},
  library: {},
  medications: [],
  careTeam: [],
  assistantActive: false,
  programDetail: {},
  programNext: {},
  programModules: {},
  programLibrary: {},
  programCareTeam: {},
  stepContent: {}
};

const setAssignments = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  return {
    ...state,
    assignments: payload as IPatientAssignment[]
  };
};

const setRun = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, pending} = payload as IRunLoadedPayload;

  return {
    ...state,
    run: {...state.run, [apId]: pending}
  };
};

const setLibrary = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, library} = payload as ILibraryLoadedPayload;

  return {
    ...state,
    library: {...state.library, [apId]: library}
  };
};

const setMedications = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  return {
    ...state,
    medications: payload as IPatientMedication[]
  };
};

const setCareTeam = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  return {
    ...state,
    careTeam: payload as IPatientCareTeamMember[]
  };
};

const setAssistantActive = ({
  state,
  payload
}: IInterventionActionHandler): IInterventionInitialState => {
  return {
    ...state,
    assistantActive: payload as boolean
  };
};

const clearInterventions = ({
  state
}: IInterventionActionHandler): IInterventionInitialState => {
  return {
    ...state,
    ...InterventionInitialState
  };
};

const setProgramDetail = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, detail} = payload as IProgramDetailLoadedPayload;

  return {...state, programDetail: {...state.programDetail, [apId]: detail}};
};

const setProgramNext = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, next} = payload as IProgramNextLoadedPayload;

  return {...state, programNext: {...state.programNext, [apId]: next}};
};

const setProgramModules = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, modules} = payload as IProgramModulesLoadedPayload;

  return {...state, programModules: {...state.programModules, [apId]: modules}};
};

const setProgramLibrary = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, items} = payload as IProgramLibraryLoadedPayload;

  return {...state, programLibrary: {...state.programLibrary, [apId]: items}};
};

const setProgramCareTeam = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, members} = payload as IProgramCareTeamLoadedPayload;

  return {...state, programCareTeam: {...state.programCareTeam, [apId]: members}};
};

const setStepContent = ({state, payload}: IInterventionActionHandler): IInterventionInitialState => {
  const {apId, stepId, stepContent} = payload as IStepContentLoadedPayload;

  return {
    ...state,
    stepContent: {
      ...state.stepContent,
      [apId]: {...(state.stepContent[apId] ?? {}), [stepId]: stepContent}
    }
  };
};

const interventionReducerHandlers = new Map([
  [ASSIGNMENTS_LOADED, setAssignments],
  [RUN_LOADED, setRun],
  [LIBRARY_LOADED, setLibrary],
  [MEDICATIONS_LOADED, setMedications],
  [CARE_TEAM_LOADED, setCareTeam],
  [ASSISTANT_SESSION_LOADED, setAssistantActive],
  [INTERVENTIONS_CLEARED, clearInterventions],
  [PROGRAM_DETAIL_LOADED, setProgramDetail],
  [PROGRAM_NEXT_LOADED, setProgramNext],
  [PROGRAM_MODULES_LOADED, setProgramModules],
  [PROGRAM_LIBRARY_LOADED, setProgramLibrary],
  [PROGRAM_CARE_TEAM_LOADED, setProgramCareTeam],
  [STEP_CONTENT_LOADED, setStepContent]
]);

const interventionReducer = (state = InterventionInitialState, action: InterventionActionsType): IInterventionInitialState => {
  const handler = interventionReducerHandlers.get(action.type);

  return (handler && {
    ...handler(<IInterventionActionHandler>{
      state,
      payload: action.payload
    })
  }) || {...state};
};

export default interventionReducer;
